/**
 * Mode dégradé Dolibarr — état partagé côté téléphone.
 *
 * Source de vérité : GET /api/easer/dolibarr-status (état vu du serveur : il ne bascule en « dégradé »
 * qu'après plusieurs échecs consécutifs ou un silence prolongé, pour éviter un bandeau qui clignote).
 * Déclencheurs d'une vérification : une réponse `dolibarr_unavailable: true` (interceptor axios dans
 * easerApi.js, via l'évènement window `easer:dolibarr-error`), un rafraîchissement périodique, le retour
 * au premier plan. À la reprise, l'évènement `easer:dolibarr-restored` est émis : les écrans se rechargent
 * et la file d'attente est renvoyée.
 */
import { easerGet, getToken, isDolibarrUnavailable } from './easerApi';

export { isDolibarrUnavailable };

const ERROR_CHECK_MIN_GAP_MS = 8000; // une erreur ne déclenche pas plus d'une vérification toutes les 8 s

const listeners = new Set();
let state = { degraded: false, downSince: null, lastSuccess: null, message: '', checkedAt: null, simulated: false };
let checking = null;
let lastErrorCheck = 0;

export const getDolibarrState = () => state;

const emit = () => listeners.forEach((cb) => { try { cb(state); } catch { /* listener error */ } });

/** Applique le verdict du serveur ; émet `easer:dolibarr-restored` sur la transition panne -> OK. */
const apply = (patch) => {
  const prev = state;
  state = { ...state, ...patch };
  if (prev.degraded && !state.degraded) {
    try { window.dispatchEvent(new Event('easer:dolibarr-restored')); } catch { /* SSR */ }
  }
  if (prev.degraded !== state.degraded || prev.downSince !== state.downSince || prev.lastSuccess !== state.lastSuccess) emit();
};

/** Abonnement aux changements d'état ; renvoie la fonction de désabonnement. */
export const subscribeDolibarr = (cb) => { listeners.add(cb); cb(state); return () => listeners.delete(cb); };

/**
 * Une réponse « Dolibarr indisponible » vient d'être reçue : on demande son verdict au serveur
 * (sans basculer localement, pour ne pas clignoter sur un échec isolé). L'action, elle, est déjà en file.
 */
export const reportDolibarrError = () => {
  const now = Date.now();
  if (now - lastErrorCheck < ERROR_CHECK_MIN_GAP_MS) return;
  lastErrorCheck = now;
  checkDolibarr();
};

/** Interroge le serveur ; résout vers l'état courant. Les appels concurrents sont fusionnés. */
export const checkDolibarr = () => {
  if (checking) return checking;
  if (!getToken()) return Promise.resolve(state);
  checking = easerGet('/api/easer/dolibarr-status')
    .then((r) => {
      const d = r.data || {};
      apply({ degraded: !!d.degraded, downSince: d.degraded ? d.down_since : null, lastSuccess: d.last_success || null,
        message: d.message || '', checkedAt: d.checked_at || new Date().toISOString(), simulated: !!d.simulated });
      return state;
    })
    .catch(() => state) // hors ligne / session expirée : on garde l'état connu
    .finally(() => { checking = null; });
  return checking;
};

// Une erreur « Dolibarr indisponible » reçue par n'importe quel appel de l'app déclenche une vérification.
if (typeof window !== 'undefined') {
  window.addEventListener('easer:dolibarr-error', () => reportDolibarrError());
}
